import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { apiService } from '../api';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  MenuItem,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import GavelIcon from '@mui/icons-material/Gavel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventIcon from '@mui/icons-material/Event';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import ReactMarkdown from 'react-markdown';

function CaseDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  
  // AI Dialog states
  const [openAnalysisDialog, setOpenAnalysisDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [analysisType, setAnalysisType] = useState('summary');
  const [analysisResult, setAnalysisResult] = useState('');

  // Schedule Dialog states
  const [openScheduleDialog, setOpenScheduleDialog] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    type: 'upcoming_meeting',
    scheduled_date: '',
    scheduled_time: '',
    description: '',
    priority: 'medium',
    tagged_members: []
  });

  useEffect(() => {
    fetchCaseData();
    fetchDocuments();
    fetchSchedules();
    fetchTeamMembers();
  }, [id]);

  // Fetch analyses after documents are loaded
  useEffect(() => {
    if (documents.length >= 0) {
      fetchAnalyses();
    }
  }, [documents.length]);

  const fetchCaseData = async () => {
    try {
      const response = await apiService.getCase(id);
      setCaseData(response.data.case);
    } catch (err) {
      setError('Failed to fetch case data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await apiService.getCaseDocuments(id);
      setDocuments(response.data.documents);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    }
  };

  const fetchAnalyses = async () => {
    try {
      console.log('Fetching all analyses for case:', id);
      
      // Fetch case-level analyses
      const caseAnalysesResponse = await apiService.getCaseAnalyses(id);
      const caseAnalyses = caseAnalysesResponse.data.analyses || [];
      console.log('Case analyses:', caseAnalyses.length);
      
      // Fetch constitutional analyses
      const constitutionalResponse = await apiService.getCaseConstitutionalAnalyses(id);
      const constitutionalAnalyses = constitutionalResponse.data.analyses || [];
      console.log('Constitutional analyses:', constitutionalAnalyses.length);
      
      // Fetch document-specific analyses
      const allDocAnalyses = [];
      const allDocConstitutional = [];
      
      console.log('Fetching analyses for', documents.length, 'documents');
      for (const doc of documents) {
        try {
          const docResponse = await apiService.getDocumentAnalyses(doc.id);
          const docStandard = docResponse.data.standard_analyses || [];
          const docConst = docResponse.data.constitutional_analyses || [];
          console.log(`Document ${doc.filename}: ${docStandard.length} standard, ${docConst.length} constitutional`);
          allDocAnalyses.push(...docStandard);
          allDocConstitutional.push(...docConst);
        } catch (err) {
          console.error(`Error fetching analyses for document ${doc.id}:`, err);
        }
      }
      
      // Combine all analyses and sort by date
      const allAnalyses = [
        ...caseAnalyses,
        ...allDocAnalyses,
        ...constitutionalAnalyses.map(a => ({ ...a, type: 'constitutional' })),
        ...allDocConstitutional.map(a => ({ ...a, type: 'constitutional' }))
      ].sort((a, b) => {
        const dateA = a.created_at?._seconds || a.created_at?.seconds || 0;
        const dateB = b.created_at?._seconds || b.created_at?.seconds || 0;
        return dateB - dateA;
      });
      
      console.log('Total analyses:', allAnalyses.length);
      setAnalyses(allAnalyses);
    } catch (err) {
      console.error('Failed to fetch analyses:', err);
    }
  };

  const fetchSchedules = async () => {
    try {
      const response = await apiService.getCaseSchedules(id);
      setSchedules(response.data.schedules || []);
    } catch (err) {
      console.error('Failed to fetch schedules:', err);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await apiService.getTeamMembers();
      setTeamMembers(response.data.members || []);
    } catch (err) {
      console.error('Failed to fetch team members:', err);
    }
  };

  const handleOpenScheduleDialog = (schedule = null) => {
    if (schedule) {
      setEditingSchedule(schedule);
      setScheduleForm({
        type: schedule.type,
        scheduled_date: schedule.scheduled_date,
        scheduled_time: schedule.scheduled_time || '',
        description: schedule.description,
        priority: schedule.priority,
        tagged_members: schedule.tagged_members || []
      });
    } else {
      setEditingSchedule(null);
      setScheduleForm({
        type: 'upcoming_meeting',
        scheduled_date: '',
        scheduled_time: '',
        description: '',
        priority: 'medium',
        tagged_members: []
      });
    }
    setOpenScheduleDialog(true);
  };

  const handleCloseScheduleDialog = () => {
    setOpenScheduleDialog(false);
    setEditingSchedule(null);
  };

  const handleSaveSchedule = async () => {
    try {
      if (editingSchedule) {
        await apiService.updateSchedule(editingSchedule.id, scheduleForm);
      } else {
        await apiService.createSchedule(id, scheduleForm);
      }
      handleCloseScheduleDialog();
      fetchSchedules();
    } catch (err) {
      setError('Failed to save schedule');
      console.error(err);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;
    
    try {
      await apiService.deleteSchedule(scheduleId);
      fetchSchedules();
    } catch (err) {
      setError('Failed to delete schedule');
      console.error(err);
    }
  };

  const handleToggleScheduleStatus = async (schedule) => {
    try {
      const newStatus = schedule.status === 'completed' ? 'pending' : 'completed';
      await apiService.updateSchedule(schedule.id, { status: newStatus });
      fetchSchedules();
    } catch (err) {
      setError('Failed to update schedule status');
      console.error(err);
    }
  };

  const onDrop = async (acceptedFiles) => {
    for (const file of acceptedFiles) {
      try {
        setUploading(true);
        await apiService.uploadDocument(id, file);
        fetchDocuments();
      } catch (err) {
        setError(`Failed to upload ${file.name}`);
        console.error(err);
      } finally {
        setUploading(false);
      }
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'image/*': ['.png', '.jpg', '.jpeg', '.gif']
    }
  });

  const handleDeleteDocument = async (docId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await apiService.deleteDocument(docId);
        fetchDocuments();
      } catch (err) {
        setError('Failed to delete document');
        console.error(err);
      }
    }
  };

  const handleAnalyzeDocument = async () => {
    try {
      setAnalyzing(true);
      const response = await apiService.analyzeDocument(selectedDocument, analysisType);
      setAnalysisResult(response.data.analysis.result);
      fetchAnalyses();
    } catch (err) {
      setError('Failed to analyze document');
      console.error(err);
    } finally {
      setAnalyzing(false);
    }
  };

  const getFileIcon = (fileType) => {
    return <DescriptionIcon />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!caseData) {
    return <Typography>Case not found</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          {caseData.title}
        </Typography>
        <Chip label={caseData.status} color="primary" />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Case Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Client:</strong> {caseData.client_name}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Case Number:</strong> {caseData.case_number}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Description:</strong> {caseData.description}
            </Typography>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AnalyticsIcon />}
              onClick={() => navigate(`/analyzer?caseId=${id}`)}
              sx={{ mt: 2 }}
            >
              Analyze Case
            </Button>
          </Paper>

          {/* Upload Area */}
          <Paper
            {...getRootProps()}
            sx={{
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'divider'
            }}
          >
            <input {...getInputProps()} />
            <UploadFileIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {uploading ? 'Uploading...' : 'Upload Documents'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Drag & drop files or click to browse
            </Typography>
            <Typography variant="caption" color="text.secondary">
              PDF, DOCX, XLSX, Images
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
              <Tab icon={<DescriptionIcon />} label="Documents" />
              <Tab icon={<AnalyticsIcon />} label="Analyses" />
              <Tab icon={<EventIcon />} label="Schedules" />
            </Tabs>

            {/* Documents Tab */}
            {tabValue === 0 && (
              <Box>
                {documents.length === 0 ? (
                  <Typography color="text.secondary" align="center">
                    No documents uploaded yet
                  </Typography>
                ) : (
                  <List>
                    {documents.map((doc) => (
                      <ListItem
                        key={doc.id}
                        secondaryAction={
                          <Box>
                            <Button
                              size="small"
                              startIcon={<SmartToyIcon />}
                              onClick={() => {
                                setSelectedDocument(doc.id);
                                setOpenAnalysisDialog(true);
                              }}
                              sx={{ mr: 1 }}
                            >
                              Analyze
                            </Button>
                            <IconButton
                              edge="end"
                              onClick={() => handleDeleteDocument(doc.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        }
                      >
                        <ListItemIcon>
                          {getFileIcon(doc.file_type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={doc.filename}
                          secondary={`${(doc.file_size / 1024).toFixed(2)} KB • ${new Date(
                            doc.uploaded_at?._seconds * 1000
                          ).toLocaleDateString()}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}

            {/* Analyses Tab */}
            {tabValue === 1 && (
              <Box>
                {analyses.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <AnalyticsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography color="text.secondary">
                      No analyses yet
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Run an analysis on a document to start building your history
                    </Typography>
                  </Box>
                ) : (
                  analyses.map((analysis, index) => {
                    const isConstitutional = analysis.type === 'constitutional' || analysis.constitutional_analysis;
                    const doc = documents.find(d => d.id === analysis.document_id);
                    
                    return (
                      <Accordion key={analysis.id} sx={{ mb: 1 }}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{ 
                            bgcolor: 'grey.50',
                            '&:hover': { bgcolor: 'grey.100' }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pr: 2 }}>
                            {isConstitutional ? (
                              <GavelIcon color="primary" fontSize="small" />
                            ) : (
                              <AnalyticsIcon color="primary" fontSize="small" />
                            )}
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {isConstitutional 
                                  ? `Constitutional - ${analysis.constitutional_analysis_type?.replace('_', ' ').toUpperCase() || 'GENERAL'}`
                                  : analysis.analysis_type?.replace('_', ' ').toUpperCase() || 'Case Analysis'
                                }
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date((analysis.created_at?._seconds || analysis.created_at?.seconds || 0) * 1000).toLocaleString()}
                                {doc && ` • ${doc.filename}`}
                              </Typography>
                            </Box>
                            {(analysis.query || analysis.custom_prompt) && (
                              <Chip 
                                label="Custom Query" 
                                size="small" 
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          {analysis.query && (
                            <Typography variant="body2" sx={{ mb: 2, p: 1.5, bgcolor: 'info.50', borderRadius: 1, fontStyle: 'italic', borderLeft: 3, borderColor: 'info.main' }}>
                              <strong>Query:</strong> {analysis.query}
                            </Typography>
                          )}
                          
                          {analysis.custom_prompt && (
                            <Typography variant="body2" sx={{ mb: 2, p: 1.5, bgcolor: 'info.50', borderRadius: 1, fontStyle: 'italic', borderLeft: 3, borderColor: 'info.main' }}>
                              <strong>Custom Prompt:</strong> {analysis.custom_prompt}
                            </Typography>
                          )}
                          
                          {isConstitutional ? (
                            <>
                              {analysis.case_summary && (
                                <>
                                  <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, fontWeight: 600, color: 'primary.main' }}>
                                    Case Summary
                                  </Typography>
                                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                                    <ReactMarkdown>{analysis.case_summary}</ReactMarkdown>
                                  </Box>
                                </>
                              )}
                              {analysis.constitutional_analysis && (
                                <>
                                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                                    Constitutional Analysis
                                  </Typography>
                                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <ReactMarkdown>{analysis.constitutional_analysis}</ReactMarkdown>
                                  </Box>
                                </>
                              )}
                            </>
                          ) : (
                            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <ReactMarkdown>{analysis.result}</ReactMarkdown>
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })
                )}
              </Box>
            )}

            {/* Schedules Tab */}
            {tabValue === 2 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">Case Schedules</Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenScheduleDialog()}
                    size="small"
                  >
                    Add Schedule
                  </Button>
                </Box>

                {schedules.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <EventIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography color="text.secondary">
                      No schedules yet
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Add a schedule to track court dates, meetings, and deadlines
                    </Typography>
                  </Box>
                ) : (
                  <List>
                    {schedules.map((schedule) => {
                      const isPast = new Date(schedule.scheduled_date) < new Date();
                      const isCompleted = schedule.status === 'completed';
                      
                      return (
                        <Card key={schedule.id} sx={{ mb: 2, border: 1, borderColor: isCompleted ? 'success.light' : isPast ? 'error.light' : 'grey.300' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Chip
                                    label={schedule.type.replace('_', ' ').toUpperCase()}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                  <Chip
                                    label={schedule.priority.toUpperCase()}
                                    size="small"
                                    color={schedule.priority === 'high' ? 'error' : schedule.priority === 'medium' ? 'warning' : 'default'}
                                  />
                                  {isCompleted && (
                                    <Chip
                                      icon={<CheckCircleIcon />}
                                      label="Completed"
                                      size="small"
                                      color="success"
                                    />
                                  )}
                                </Box>
                                <Typography variant="h6" sx={{ mb: 1, textDecoration: isCompleted ? 'line-through' : 'none' }}>
                                  {schedule.description}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  <strong>Date:</strong> {new Date(schedule.scheduled_date).toLocaleDateString()}
                                  {schedule.scheduled_time && ` at ${schedule.scheduled_time}`}
                                </Typography>
                                {schedule.tagged_members && schedule.tagged_members.length > 0 && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                    <PersonIcon fontSize="small" color="action" />
                                    <Typography variant="caption" color="text.secondary">
                                      Tagged: {schedule.tagged_members.map(memberId => {
                                        const member = teamMembers.find(m => m.id === memberId);
                                        return member ? member.name : 'Unknown';
                                      }).join(', ')}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleToggleScheduleStatus(schedule)}
                                  color={isCompleted ? 'default' : 'success'}
                                >
                                  <CheckCircleIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenScheduleDialog(schedule)}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteSchedule(schedule.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </List>
                )}
              </Box>
            )}

          </Paper>
        </Grid>
      </Grid>

      {/* Schedule Dialog */}
      <Dialog
        open={openScheduleDialog}
        onClose={handleCloseScheduleDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingSchedule ? 'Edit Schedule' : 'Add New Schedule'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              select
              label="Type"
              value={scheduleForm.type}
              onChange={(e) => setScheduleForm({ ...scheduleForm, type: e.target.value })}
              fullWidth
            >
              <MenuItem value="upcoming_meeting">Upcoming Meeting</MenuItem>
              <MenuItem value="submission">Submission of Document</MenuItem>
              <MenuItem value="court_date">Court Date</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>

            <TextField
              type="date"
              label="Date"
              value={scheduleForm.scheduled_date}
              onChange={(e) => setScheduleForm({ ...scheduleForm, scheduled_date: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              required
            />

            <TextField
              type="time"
              label="Time (Optional)"
              value={scheduleForm.scheduled_time}
              onChange={(e) => setScheduleForm({ ...scheduleForm, scheduled_time: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Description"
              value={scheduleForm.description}
              onChange={(e) => setScheduleForm({ ...scheduleForm, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
              required
            />

            <TextField
              select
              label="Priority"
              value={scheduleForm.priority}
              onChange={(e) => setScheduleForm({ ...scheduleForm, priority: e.target.value })}
              fullWidth
              required
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </TextField>

            <TextField
              select
              label="Tag Team Members"
              value={scheduleForm.tagged_members}
              onChange={(e) => setScheduleForm({ ...scheduleForm, tagged_members: e.target.value })}
              fullWidth
              SelectProps={{
                multiple: true,
                renderValue: (selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const member = teamMembers.find(m => m.id === value);
                      return <Chip key={value} label={member?.name || value} size="small" />;
                    })}
                  </Box>
                )
              }}
            >
              {teamMembers.map((member) => (
                <MenuItem key={member.id} value={member.id}>
                  {member.name} - {member.role}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseScheduleDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveSchedule}
            disabled={!scheduleForm.scheduled_date || !scheduleForm.description || !scheduleForm.priority}
          >
            {editingSchedule ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Analysis Dialog */}
      <Dialog
        open={openAnalysisDialog}
        onClose={() => setOpenAnalysisDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Analyze Document with AI</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Analysis Type"
            value={analysisType}
            onChange={(e) => setAnalysisType(e.target.value)}
            margin="normal"
          >
            <MenuItem value="summary">Summary</MenuItem>
            <MenuItem value="key_points">Key Points</MenuItem>
            <MenuItem value="risks">Risk Analysis</MenuItem>
            <MenuItem value="recommendations">Recommendations</MenuItem>
            <MenuItem value="timeline">Timeline</MenuItem>
            <MenuItem value="parties">Parties Involved</MenuItem>
          </TextField>

          {analysisResult && (
            <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Analysis Result
              </Typography>
              <ReactMarkdown>{analysisResult}</ReactMarkdown>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenAnalysisDialog(false);
            setAnalysisResult('');
          }}>
            Close
          </Button>
          <Button
            onClick={handleAnalyzeDocument}
            variant="contained"
            disabled={analyzing}
          >
            {analyzing ? 'Analyzing...' : 'Analyze'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CaseDetails;
